import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { chatbotService } from '../../../services/chatbotService';
import { LogoUploader, S3Params } from '../../LogoUploader'; 

interface GeneralTabProps {
  chatbot: any;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ chatbot }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [greeting, setGreeting] = useState('Hello');
  const [placeholder, setPlaceholder] = useState('Type here...');
  const [logo, setLogo] = useState<S3Params | null>(null); 
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!chatbot) return;
    setName(chatbot.name ?? '');
    setDescription(chatbot.description ?? '');
    setGreeting(chatbot.config?.greeting ?? 'Hello');
    setPlaceholder(chatbot.config?.placeholder ?? 'Type here...');
    setLogo(chatbot.s3ObjectData ?? null);
  }, [chatbot?.id, chatbot?.updatedAt, chatbot]);

  const save = async () => {
    if (!chatbot?.id) {
      toast.error('Missing chatbot id — cannot save.');
      return;
    }

    setIsSaving(true);
    try {
      const payload: any = {
        name,
        description,
        isActive: chatbot.isActive ?? true,
        state: chatbot.state ?? 'draft',
        version: chatbot.version ?? 1,
        config: {
          ...(chatbot.config ?? {}),
          greeting: greeting ?? '',
          placeholder: placeholder ?? '',
        },
        s3ObjectData: logo ?? undefined,
      };

      const resp = await chatbotService.updateChatbot(encodeURIComponent(chatbot.id), payload);
      const updated = (resp as any)?.data ?? resp;

      if (updated) {
        setName(updated.name ?? name);
        setDescription(updated.description ?? description);
        setGreeting(updated.config?.greeting ?? greeting);
        setPlaceholder(updated.config?.placeholder ?? placeholder);
        setLogo(updated.s3ObjectData ?? logo);
      }

      toast.success('General settings saved.');
    } catch (err: any) {
      console.error('Failed to save general settings', err);
      toast.error(err?.message ?? 'Failed to save general settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-agentic-text-primary mb-1">
            Chatbot Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-agentic-text-primary mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
          />
        </div>

        {/* Logo Uploader */}
        <div>
          <LogoUploader
            label="Chatbot Logo"
            value={logo}
            onChange={(val) => setLogo(val)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Greeting Message
            </label>
            <input
              value={greeting}
              onChange={(e) => setGreeting(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Placeholder Text
            </label>
            <input
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={isSaving}
          className={`px-4 py-2 rounded-lg text-white ${
            isSaving ? 'bg-gray-300 cursor-not-allowed' : 'bg-agentic-primary-blue hover:bg-agentic-primary-blue/90'
          }`}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default GeneralTab;
